/**
 * Esquemas Zod del dominio de Inventario (alta de rollos + consumo).
 *
 * Validan la entrada del formulario ANTES de llamar a la API (`docs/
 * frontend_conventions.md`, Validación). El tipo del formulario se **infiere** del
 * esquema (`z.infer`), no se escribe a mano. Las reglas replican las del
 * `InventarioService` (`add_rollo`/`consume` en `inventario_service.py`):
 *
 *  - alta galv: requiere calibre, formato y kg > 0;
 *  - alta acero: requiere perfil y metros > 0;
 *  - consumo: requiere una cantidad > 0 (kg para galv, metros para acero).
 *
 * El backend ignora silenciosamente los lotes con cantidad <= 0 (comportamiento
 * del service); aquí se valida antes para dar retroalimentación inmediata.
 */
import { z } from "zod";

/** Campo de cantidad positiva en texto (acepta decimales, > 0). */
const cantidadField = z
  .string()
  .trim()
  .min(1, "Indica una cantidad")
  .refine((value) => {
    const n = Number(value.replace(",", "."));
    return Number.isFinite(n) && n > 0;
  }, "Indica una cantidad mayor que 0");

/** Alta de rollo galvanizado: calibre + formato + kg. */
export const addGalvSchema = z.object({
  calibre: z.string().trim().min(1, "Selecciona el calibre"),
  fmt: z.string().trim().min(1, "Selecciona el formato"),
  kg: cantidadField,
  nota: z.string().trim().optional().default(""),
});

/** Alta de rollo de acero/aluminio: perfil + metros. */
export const addAceroSchema = z.object({
  perfil_id: z.string().trim().min(1, "Selecciona el perfil"),
  metros: cantidadField,
  nota: z.string().trim().optional().default(""),
});

/** Consumo de un rollo: solo la cantidad (el rollo lo fija la tarjeta). */
export const consumeSchema = z.object({
  cantidad: cantidadField,
});

/** Valores del formulario de alta galv (inferidos del esquema). */
export type AddGalvValues = z.infer<typeof addGalvSchema>;
/** Valores del formulario de alta acero (inferidos del esquema). */
export type AddAceroValues = z.infer<typeof addAceroSchema>;
/** Valores del formulario de consumo (inferidos del esquema). */
export type ConsumeValues = z.infer<typeof consumeSchema>;
